import React, { useEffect, useRef } from "react";
import { FiAlertTriangle, FiCheckCircle, FiHelpCircle, FiTrash2, FiX } from "react-icons/fi";

/*
 * Shared confirmation dialog.
 *
 * Props (all existing ones still work):
 *   show, title, message, children, onConfirm, onCancel,
 *   confirmText, cancelText, confirmDisabled, contentClassName
 * New, optional:
 *   variant: "danger" | "success" | "default"  (inferred from the title when omitted)
 *   icon:    a react-icons component to override the default icon
 */

const inferVariant = (title = "") => {
    const text = String(title).toLowerCase();
    if (/(delete|reject|remove|close|cancel|deactivate)/.test(text)) return "danger";
    if (/(accept|approve|confirm acceptance)/.test(text)) return "success";
    return "default";
};

const DEFAULT_ICONS = {
    danger: (title = "") => (/(delete|remove)/i.test(String(title)) ? FiTrash2 : FiAlertTriangle),
    success: () => FiCheckCircle,
    default: () => FiHelpCircle,
};

export default function ConfirmationModal({
    show,
    title = "Confirm Action",
    message,
    children,
    onConfirm,
    onCancel,
    confirmText = "Confirm",
    cancelText = "Cancel",
    confirmDisabled = false,
    contentClassName = "",
    variant,
    icon,
}) {
    const cancelRef = useRef(null);
    const tone = variant || inferVariant(title);
    const Icon = icon || DEFAULT_ICONS[tone]?.(title) || FiHelpCircle;
    const titleId = "qcm-title";

    // Esc to close, lock page scroll, and focus the safe (Cancel) button.
    // Cancel is never blocked: some pages use confirmDisabled for validation
    // (e.g. a required reason), and pages that must not close mid-request
    // already guard their own onCancel.
    useEffect(() => {
        if (!show) return undefined;

        const handleKey = (event) => {
            if (event.key === "Escape") onCancel?.();
        };
        const previousOverflow = document.body.style.overflow;

        document.addEventListener("keydown", handleKey);
        document.body.style.overflow = "hidden";
        cancelRef.current?.focus();

        return () => {
            document.removeEventListener("keydown", handleKey);
            document.body.style.overflow = previousOverflow;
        };
    }, [show, onCancel]);

    if (!show) return null;

    const handleBackdrop = (event) => {
        if (event.target === event.currentTarget) onCancel?.();
    };

    return (
        <div className="modalOverlay qcm-overlay" onMouseDown={handleBackdrop}>
            <div
                className={`modalContent qcm-modal qcm-${tone} ${contentClassName}`}
                role="alertdialog"
                aria-modal="true"
                aria-labelledby={titleId}
            >
                <button
                    type="button"
                    className="qcm-close"
                    onClick={onCancel}
                    aria-label="Close"
                >
                    <FiX />
                </button>

                <div className="qcm-icon" aria-hidden="true">
                    <span>
                        <Icon />
                    </span>
                </div>

                <h5 id={titleId} className="qcm-title">
                    {title}
                </h5>

                {children ? (
                    <div className="qcm-content">{children}</div>
                ) : (
                    message && (typeof message === "string" ? (
                        <p className="qcm-message">{message}</p>
                    ) : (
                        <div className="qcm-content">{message}</div>
                    ))
                )}

                <div className="modalActions qcm-actions">
                    <button
                        ref={cancelRef}
                        type="button"
                        className="btn btn-cancel qcm-btn qcm-btn-cancel"
                        onClick={onCancel}
                    >
                        {cancelText}
                    </button>
                    <button
                        type="button"
                        className="btn btn-confirm qcm-btn qcm-btn-confirm"
                        onClick={onConfirm}
                        disabled={confirmDisabled}
                    >
                        {confirmText}
                    </button>
                </div>
            </div>
        </div>
    );
}
